import { format, parseISO } from 'date-fns';
import { calRangeType } from '../../services/View_Slice';
import { getMonday } from './dateHelper';

/**
 * Converts a full date string to the appropriate format for the current calendar granularity.
 *
 * The calendar uses different date formats for different view types:
 * - Year view: "yyyy" (e.g., "2024")
 * - Month view: "yyyy-MM" (e.g., "2024-03")
 * - Week view: "yyyy-MM-dd" (Monday of the week containing the date)
 * - Day view: "yyyy-MM-dd" (e.g., "2024-03-15")
 *
 * @param dateStr - Full date string in "yyyy-MM-dd" format
 * @param granularity - Current calendar view type (calRangeType enum)
 * @returns Formatted date string appropriate for the view type
 *
 * @example
 * convertDateToGranularity('2024-03-15', calRangeType.month) // Returns '2024-03'
 * convertDateToGranularity('2024-03-15', calRangeType.year)  // Returns '2024'
 * convertDateToGranularity('2024-03-15', calRangeType.week)  // Returns '2024-03-11' (Monday of that week)
 */
export function convertDateToGranularity(
    dateStr: string,
    granularity: calRangeType | undefined
): string {
    // Parse the input date
    // Handle various input formats (with or without time component)
    const cleanDateStr = dateStr.split('T')[0]; // Remove time component if present
    const date = parseISO(cleanDateStr);

    // Validate the parsed date
    if (isNaN(date.getTime())) {
        console.error(`[dateGranularity] Invalid date string: ${dateStr}`);
        // Return the original string as fallback
        return dateStr;
    }

    switch (granularity) {
        case calRangeType.year:
            // Year view format: "yyyy"
            return format(date, 'yyyy');

        case calRangeType.month:
            // Month view format: "yyyy-MM"
            return format(date, 'yyyy-MM');

        case calRangeType.week:
            // Week view format: "yyyy-MM-dd" (Monday of the week)
            // Use the existing getMonday helper for consistency
            const monday = getMonday(date);
            return format(monday, 'yyyy-MM-dd');

        case calRangeType.day:
            // Day view format: "yyyy-MM-dd"
            return format(date, 'yyyy-MM-dd');

        default:
            // Default to month format if granularity is undefined
            console.log(`[dateGranularity] Granularity undefined, defaulting to month format`);
            return format(date, 'yyyy-MM');
    }
}

/**
 * Extracts date components from a formatted date string based on granularity.
 * Useful for converting between different date formats.
 *
 * @param dateStr - Date string in granularity-specific format
 * @param granularity - The granularity the date string is in
 * @returns Object with year, month, day components (as available)
 */
export function extractDateComponents(
    dateStr: string,
    granularity: calRangeType | undefined
): { year: string; month?: string; day?: string } {
    const parts = dateStr.split('-');

    switch (granularity) {
        case calRangeType.year:
            return { year: parts[0] };

        case calRangeType.month:
            return {
                year: parts[0],
                month: parts[1]
            };

        case calRangeType.week:
        case calRangeType.day:
            return {
                year: parts[0],
                month: parts[1],
                day: parts[2]
            };

        default:
            return { year: parts[0], month: parts[1] };
    }
}
